/**
 * Blended WHLS Fee Calculator for Direct Products
 * 
 * This file contains the calculation logic for Blended WHLS Fee based on:
 * - Target Launch Date
 * - Suggested Bid Price
 * - VA Annual Usage
 * - DOD Annual Usage
 * - VA TMS Percentage
 * - DOD TMS Percentage
 * - Contract Type (for calculateAdjustedBidPrice)
 * 
 * The calculation uses different rate schedules depending on whether the launch date
 * is after 8/1/2024, and calculates a weighted average based on VA and DOD usage.
 */

/**
 * Calculate Blended WHLS Fee
 * 
 * @param targetLaunchDate - Target launch date (Date object or string)
 * @param suggestedBidPrice - Suggested bid price
 * @param vaAnnualUsage - VA annual usage
 * @param dodAnnualUsage - DOD annual usage
 * @param vaTmsPercent - VA TMS percentage
 * @param dodTmsPercent - DOD TMS percentage
 * @returns The calculated blended WHLS fee amount
 */
export const calculateBlendedWhlsFee = (
  targetLaunchDate: Date | string | null | undefined,
  suggestedBidPrice: number,
  vaAnnualUsage: number,
  dodAnnualUsage: number,
  vaTmsPercent: number,
  dodTmsPercent: number
): number => {
  // Validate inputs
  if (!targetLaunchDate || !suggestedBidPrice || suggestedBidPrice <= 0) {
    return 0;
  }

  // Parse target launch date
  const launchDate = parseLaunchDate(targetLaunchDate);
  if (!launchDate) {
    return 0;
  }

  // Check if launch date is after 8/1/2024
  const aug2024 = new Date(2024, 7, 1); // Month is 0-indexed, so 7 = August
  const isAfterAug2024 = launchDate > aug2024;

  // Calculate weighted denominator (total usage-weighted TMS)
  const totalWeightedTms = dodAnnualUsage * dodTmsPercent + vaAnnualUsage * vaTmsPercent;

  // If total weighted TMS is 0, return 0
  if (totalWeightedTms === 0) {
    return 0;
  }

  // Calculate weights for VA and DOD portions
  const vaWeight = (vaAnnualUsage * vaTmsPercent) / totalWeightedTms;
  const dodWeight = (dodAnnualUsage * dodTmsPercent) / totalWeightedTms;

  // Get rates based on launch date
  let vaRate: number;
  let dodRate: number;

  if (isAfterAug2024) {
    vaRate = calculateVaRateAfterAug2024(launchDate);
    dodRate = calculateDodRate(launchDate);
  } else {
    vaRate = calculateVaRateBeforeAug2024(launchDate);
    dodRate = calculateDodRate(launchDate);
  }

  // Calculate weighted average rate
  const weightedAverageRate = vaWeight * vaRate + dodWeight * dodRate;

  // Final calculation: Suggested Bid Price * Weighted Average Rate
  return suggestedBidPrice * weightedAverageRate;
};

/**
 * Parse launch date from various formats
 */
const parseLaunchDate = (date: Date | string | null | undefined): Date | null => {
  if (!date) return null;

  if (date instanceof Date) {
    return isNaN(date.getTime()) ? null : date;
  }

  if (typeof date === 'string') {
    // Try parsing various date formats
    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
};

/**
 * Calculate VA Rate for launch dates after 8/1/2024
 * Rate schedule: 1%, 7.5%, 7.5%, 7.5%, 8.5%, 9.5%
 * 
 * @param launchDate - Target launch date
 * @returns VA rate as a decimal (e.g., 0.01 for 1%)
 */
const calculateVaRateAfterAug2024 = (launchDate: Date): number => {
  const apr30_2023 = new Date(2023, 3, 30); // April 30, 2023
  const apr30_2024 = new Date(2024, 3, 30); // April 30, 2024
  const apr30_2025 = new Date(2025, 3, 30); // April 30, 2025
  const apr30_2026 = new Date(2026, 3, 30); // April 30, 2026
  const apr30_2027 = new Date(2027, 3, 30); // April 30, 2027
  const apr30_2028 = new Date(2028, 3, 30); // April 30, 2028

  // Calculate anniversary date (1 year after launch)
  const anniversaryDate = new Date(launchDate);
  anniversaryDate.setFullYear(anniversaryDate.getFullYear() + 1);

  if (launchDate <= apr30_2023) {
    // Calculate prorated rate for period ending 4/30/2024
    const endDate = apr30_2024;
    const daysInPeriod = Math.min(
      Math.max(0, (endDate.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1),
      365
    );
    const rate = Math.round((daysInPeriod / 365) * 1 * 10) / 10; // 1%
    return rate / 100;
  } else if (launchDate <= apr30_2024) {
    // Split between 1% and 7.5% periods
    const period1End = apr30_2024;
    const period2Start = new Date(2024, 4, 1); // May 1, 2024
    const daysInPeriod1 = Math.max(0, (period1End.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1) / 365 * 1;
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - period2Start.getTime()) / (1000 * 60 * 60 * 24) + 1) / 365 * 7.5;
    const rate = Math.round((daysInPeriod1 + daysInPeriod2) * 10) / 10;
    return rate / 100;
  } else if (launchDate <= apr30_2025) {
    // Split between 7.5% periods
    const period1End = apr30_2025;
    const period2Start = new Date(2025, 4, 1); // May 1, 2025
    const daysInPeriod1 = Math.max(0, (period1End.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1) / 365 * 7.5;
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - period2Start.getTime()) / (1000 * 60 * 60 * 24) + 1) / 365 * 7.5;
    const rate = Math.round((daysInPeriod1 + daysInPeriod2) * 10) / 10;
    return rate / 100;
  } else if (launchDate <= apr30_2026) {
    // Split between 7.5% and 7.5% periods
    const period1End = apr30_2026;
    const period2Start = new Date(2026, 4, 1); // May 1, 2026
    const daysInPeriod1 = Math.max(0, (period1End.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1) / 365 * 7.5;
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - period2Start.getTime()) / (1000 * 60 * 60 * 24) + 1) / 365 * 7.5;
    const rate = Math.round((daysInPeriod1 + daysInPeriod2) * 10) / 10;
    return rate / 100;
  } else if (launchDate <= apr30_2027) {
    // Split between 7.5% and 8.5% periods
    const period1End = apr30_2027;
    const period2Start = new Date(2027, 4, 1); // May 1, 2027
    const daysInPeriod1 = Math.max(0, (period1End.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1) / 365 * 7.5;
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - period2Start.getTime()) / (1000 * 60 * 60 * 24) + 1) / 365 * 8.5;
    const rate = Math.round((daysInPeriod1 + daysInPeriod2) * 10) / 10;
    return rate / 100;
  } else if (launchDate <= apr30_2028) {
    // Split between 8.5% and 9.5% periods
    const period1End = apr30_2028;
    const period2Start = new Date(2028, 4, 1); // May 1, 2028
    const daysInPeriod1 = Math.max(0, (period1End.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1) / 365 * 8.5;
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - period2Start.getTime()) / (1000 * 60 * 60 * 24) + 1) / 365 * 9.5;
    const rate = Math.round((daysInPeriod1 + daysInPeriod2) * 10) / 10;
    return rate / 100;
  } else {
    // Default rate for dates after 4/30/2028
    return 9.5 / 100;
  }
};

/**
 * Calculate VA Rate for launch dates on or before 8/1/2024
 * Rate schedule: 1%, 5.5%, 6.5%, 7.5%, 7.5%, 8.5%
 * 
 * @param launchDate - Target launch date
 * @returns VA rate as a decimal (e.g., 0.01 for 1%)
 */
const calculateVaRateBeforeAug2024 = (launchDate: Date): number => {
  const apr30_2023 = new Date(2023, 3, 30); // April 30, 2023
  const apr30_2024 = new Date(2024, 3, 30); // April 30, 2024
  const apr30_2025 = new Date(2025, 3, 30); // April 30, 2025
  const apr30_2026 = new Date(2026, 3, 30); // April 30, 2026
  const apr30_2027 = new Date(2027, 3, 30); // April 30, 2027
  const apr30_2028 = new Date(2028, 3, 30); // April 30, 2028

  // Calculate anniversary date (1 year after launch)
  const anniversaryDate = new Date(launchDate);
  anniversaryDate.setFullYear(anniversaryDate.getFullYear() + 1);

  if (launchDate <= apr30_2023) {
    // Calculate prorated rate for period ending 4/30/2024
    const endDate = apr30_2024;
    const daysInPeriod = Math.min(
      Math.max(0, (endDate.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1),
      365
    );
    const rate = Math.round((daysInPeriod / 365) * 1 * 10) / 10; // 1%
    return rate / 100;
  } else if (launchDate <= apr30_2024) {
    // Split between 1% and 5.5% periods
    const period1End = apr30_2024;
    const period2Start = new Date(2024, 4, 1); // May 1, 2024
    const daysInPeriod1 = Math.max(0, (period1End.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1) / 365 * 1;
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - period2Start.getTime()) / (1000 * 60 * 60 * 24) + 1) / 365 * 5.5;
    const rate = Math.round((daysInPeriod1 + daysInPeriod2) * 10) / 10;
    return rate / 100;
  } else if (launchDate <= apr30_2025) {
    // Split between 5.5% and 6.5% periods
    const period1End = apr30_2025;
    const period2Start = new Date(2025, 4, 1); // May 1, 2025
    const daysInPeriod1 = Math.max(0, (period1End.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1) / 365 * 5.5;
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - period2Start.getTime()) / (1000 * 60 * 60 * 24) + 1) / 365 * 6.5;
    const rate = Math.round((daysInPeriod1 + daysInPeriod2) * 10) / 10;
    return rate / 100;
  } else if (launchDate <= apr30_2026) {
    // Split between 6.5% and 7.5% periods
    const period1End = apr30_2026;
    const period2Start = new Date(2026, 4, 1); // May 1, 2026
    const daysInPeriod1 = Math.max(0, (period1End.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1) / 365 * 6.5;
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - period2Start.getTime()) / (1000 * 60 * 60 * 24) + 1) / 365 * 7.5;
    const rate = Math.round((daysInPeriod1 + daysInPeriod2) * 10) / 10;
    return rate / 100;
  } else if (launchDate <= apr30_2027) {
    // Split between 7.5% and 7.5% periods
    const period1End = apr30_2027;
    const period2Start = new Date(2027, 4, 1); // May 1, 2027
    const daysInPeriod1 = Math.max(0, (period1End.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1) / 365 * 7.5;
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - period2Start.getTime()) / (1000 * 60 * 60 * 24) + 1) / 365 * 7.5;
    const rate = Math.round((daysInPeriod1 + daysInPeriod2) * 10) / 10;
    return rate / 100;
  } else if (launchDate <= apr30_2028) {
    // Split between 7.5% and 8.5% periods
    const period1End = apr30_2028;
    const period2Start = new Date(2028, 4, 1); // May 1, 2028
    const daysInPeriod1 = Math.max(0, (period1End.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1) / 365 * 7.5;
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - period2Start.getTime()) / (1000 * 60 * 60 * 24) + 1) / 365 * 8.5;
    const rate = Math.round((daysInPeriod1 + daysInPeriod2) * 10) / 10;
    return rate / 100;
  } else {
    // Default rate for dates after 4/30/2028
    return 8.5 / 100;
  }
};

/**
 * Calculate DOD Rate
 * Uses 3% or 3.25% based on date ranges
 * 
 * @param launchDate - Target launch date
 * @returns DOD rate as a decimal (e.g., 0.03 for 3%)
 */
const calculateDodRate = (launchDate: Date): number => {
  const dec31_2025 = new Date(2025, 11, 31); // December 31, 2025
  const jan1_2026 = new Date(2026, 0, 1); // January 1, 2026

  if (launchDate <= dec31_2025) {
    // Calculate 12-month anniversary date
    const anniversaryDate = new Date(launchDate);
    anniversaryDate.setMonth(anniversaryDate.getMonth() + 12);
    anniversaryDate.setDate(anniversaryDate.getDate() - 1);

    if (anniversaryDate <= dec31_2025) {
      // Entire first year is within 2025, use 3%
      return 3 / 100;
    } else if (anniversaryDate >= jan1_2026) {
      // First year spans across 2025-2026 boundary
      // Calculate weighted average
      const daysIn2025 = Math.max(0, (jan1_2026.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24));
      const daysIn2026 = Math.max(0, (anniversaryDate.getTime() - jan1_2026.getTime()) / (1000 * 60 * 60 * 24) + 1);
      const totalDays = daysIn2025 + daysIn2026;

      if (totalDays === 0) {
        return 3.25 / 100;
      }

      // Calculate percentage in each year (round up to nearest integer)
      const percentIn2025 = Math.ceil((daysIn2025 / totalDays) * 100);
      const percentIn2026 = 100 - percentIn2025;

      // Calculate weighted rate as percentage
      const weightedRate = (percentIn2025 * 3 + percentIn2026 * 3.25) / 100;
      
      // Convert to decimal and round up to 4 decimal places
      // ROUNDUP(weightedRate / 100, 4) means round up to 4 decimal places
      const rate = Math.ceil((weightedRate / 100) * 10000) / 10000;
      return rate;
    } else {
      // Edge case
      return 3.25 / 100;
    }
  } else {
    // Launch date after 2025, use 3.25%
    return 3.25 / 100;
  }
};

/**
 * Calculate Adjusted Bid Price (Blended Whole Seller Fee) for Direct Products
 * 
 * This function calculates a weighted, date-adjusted bid price using VA and DOD annual usage
 * data with TMS percentages. It applies different interest rate schedules based on contract
 * type and target launch date.
 * 
 * @param contractType - The contract type (e.g., 'National Contract')
 * @param targetLaunchDate - Target launch date (Date object or string YYYY-MM-DD)
 * @param suggestedBidPrice - The base suggested bid price to be adjusted
 * @param vaAnnualUsage - VA (Veterans Affairs) Annual Usage
 * @param dodAnnualUsage - DOD (Department of Defense) Annual Usage
 * @param vaTmsPercent - VA TMS percentage (as decimal, e.g., 0.05 for 5%)
 * @param dodTmsPercent - DOD TMS percentage (as decimal, e.g., 0.03 for 3%)
 * @returns The calculated adjusted bid price (Blended Whole Seller Fee)
 */
export const calculateAdjustedBidPrice = (
  contractType: string,
  targetLaunchDate: Date | string | null | undefined,
  suggestedBidPrice: number,
  vaAnnualUsage: number,
  dodAnnualUsage: number,
  vaTmsPercent: number,
  dodTmsPercent: number
): number => {
  // Validate inputs
  if (!targetLaunchDate || !suggestedBidPrice || suggestedBidPrice <= 0) {
    return 0;
  }

  // Parse target launch date
  const launchDate = parseLaunchDate(targetLaunchDate);
  if (!launchDate) {
    return 0;
  }

  // Step 1: Calculate Group 1 = VA Annual Usage × VA TMS %
  const group1 = vaAnnualUsage * vaTmsPercent;
  
  // Step 2: Calculate Group 2 = DOD Annual Usage × DOD TMS %
  const group2 = dodAnnualUsage * dodTmsPercent;
  
  // Step 3: Calculate Total = Group 1 + Group 2
  const total = group1 + group2;
  
  // Handle division by zero
  if (total === 0) {
    return 0;
  }

  // Step 4: Calculate Weight 1 = Group 1 / Total
  const weight1 = group1 / total;
  
  // Step 5: Calculate Weight 2 = Group 2 / Total
  const weight2 = group2 / total;

  // Step 6: Determine if Contract Type = 'National Contract' AND Target Launch Date > August 1, 2024 to choose rate path
  const nationalContractType = 'National Contract';
  const thresholdDate = new Date(2024, 7, 1); // August 1, 2024 (month is 0-indexed)
  const isNationalContractAfterAug2024 = contractType === nationalContractType && launchDate > thresholdDate;

  // Step 7: Calculate Group 1 percentage based on target launch date and selected rate schedule
  let group1Percentage: number;
  if (isNationalContractAfterAug2024) {
    group1Percentage = calculateDirectGroup1RatePath1(launchDate);
  } else {
    group1Percentage = calculateDirectGroup1RatePath2(launchDate);
  }

  // Step 8: Calculate Group 2 percentage based on target launch date and selected rate schedule
  let group2Percentage: number;
  if (isNationalContractAfterAug2024) {
    group2Percentage = calculateDirectGroup2RatePath1(launchDate);
  } else {
    group2Percentage = calculateDirectGroup2RatePath2(launchDate);
  }

  // Step 9: Calculate combined percentage = (Weight 1 × Group 1 Percentage) + (Weight 2 × Group 2 Percentage)
  const combinedPercentage = (weight1 * group1Percentage) + (weight2 * group2Percentage);

  // Step 10: Calculate final result = Suggested Bid Price × Combined Percentage
  return suggestedBidPrice * combinedPercentage;
};

/**
 * Calculate Group 1 Rate for Path 1 (National Contract after August 1, 2024)
 * Rate schedule: 1%, 1%/5.5%, 5.5%/6.5%, 6.5%/7.5%, 7.5%/7.5%, 7.5%/8.5%, 8.5%
 */
const calculateDirectGroup1RatePath1 = (launchDate: Date): number => {
  // Calculate 1-year anniversary date from launch date
  const anniversaryDate = new Date(launchDate);
  anniversaryDate.setFullYear(anniversaryDate.getFullYear() + 1);

  // Rate transition dates
  const apr30_2023 = new Date(2023, 3, 30); // April 30, 2023
  const apr30_2024 = new Date(2024, 3, 30); // April 30, 2024
  const apr30_2025 = new Date(2025, 3, 30); // April 30, 2025
  const apr30_2026 = new Date(2026, 3, 30); // April 30, 2026
  const apr30_2027 = new Date(2027, 3, 30); // April 30, 2027
  const apr30_2028 = new Date(2028, 3, 30); // April 30, 2028
  const may1_2024 = new Date(2024, 4, 1); // May 1, 2024
  const may1_2025 = new Date(2025, 4, 1); // May 1, 2025
  const may1_2026 = new Date(2026, 4, 1); // May 1, 2026
  const may1_2027 = new Date(2027, 4, 1); // May 1, 2027
  const may1_2028 = new Date(2028, 4, 1); // May 1, 2028

  if (launchDate <= apr30_2023) {
    // Special logic: Check if April 30, 2024 is before or equal to (launch date + 1 year)
    const daysUntilApr30_2024 = Math.max(0, (apr30_2024.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    if (apr30_2024 <= anniversaryDate) {
      // If yes, use (days to April 30, 2024 + 1) / 365 × 1%
      return Math.round((daysUntilApr30_2024 / 365) * 1 * 10) / 10 / 100;
    } else {
      // If no, use MIN((days to April 30, 2024 + 1) / 365, 1) × 1%
      return Math.round(Math.min(daysUntilApr30_2024 / 365, 1) * 1 * 10) / 10 / 100;
    }
  } else if (launchDate <= apr30_2024) {
    // 2023_04_30_to_2024_04_30: period1: 0.01, period2: 0.055
    const daysInPeriod1 = Math.max(0, (apr30_2024.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - may1_2024.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const rate = (daysInPeriod1 / 365 * 1) + (daysInPeriod2 / 365 * 5.5);
    return Math.round(rate * 10) / 10 / 100;
  } else if (launchDate <= apr30_2025) {
    // 2024_04_30_to_2025_04_30: period1: 0.055, period2: 0.065
    const daysInPeriod1 = Math.max(0, (apr30_2025.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - may1_2025.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const rate = (daysInPeriod1 / 365 * 5.5) + (daysInPeriod2 / 365 * 6.5);
    return Math.round(rate * 10) / 10 / 100;
  } else if (launchDate <= apr30_2026) {
    // 2025_04_30_to_2026_04_30: period1: 0.065, period2: 0.075
    const daysInPeriod1 = Math.max(0, (apr30_2026.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - may1_2026.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const rate = (daysInPeriod1 / 365 * 6.5) + (daysInPeriod2 / 365 * 7.5);
    return Math.round(rate * 10) / 10 / 100;
  } else if (launchDate <= apr30_2027) {
    // 2026_04_30_to_2027_04_30: period1: 0.075, period2: 0.075
    const daysInPeriod1 = Math.max(0, (apr30_2027.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - may1_2027.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const rate = (daysInPeriod1 / 365 * 7.5) + (daysInPeriod2 / 365 * 7.5);
    return Math.round(rate * 10) / 10 / 100;
  } else if (launchDate <= apr30_2028) {
    // 2027_04_30_to_2028_04_30: period1: 0.075, period2: 0.085
    const daysInPeriod1 = Math.max(0, (apr30_2028.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - may1_2028.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const rate = (daysInPeriod1 / 365 * 7.5) + (daysInPeriod2 / 365 * 8.5);
    return Math.round(rate * 10) / 10 / 100;
  } else {
    // after_2028_04_30: 0.085 (8.5%)
    return 0.085;
  }
};

/**
 * Calculate Group 1 Rate for Path 2 (Other cases - not National Contract after Aug 2024)
 * Rate schedule: 1%, 1%/7.5%, 7.5%/7.5%, 7.5%/7.5%, 7.5%/8.5%, 8.5%/9.5%, 9.5%
 */
const calculateDirectGroup1RatePath2 = (launchDate: Date): number => {
  // Calculate 1-year anniversary date from launch date
  const anniversaryDate = new Date(launchDate);
  anniversaryDate.setFullYear(anniversaryDate.getFullYear() + 1);

  // Rate transition dates
  const apr30_2023 = new Date(2023, 3, 30); // April 30, 2023
  const apr30_2024 = new Date(2024, 3, 30); // April 30, 2024
  const apr30_2025 = new Date(2025, 3, 30); // April 30, 2025
  const apr30_2026 = new Date(2026, 3, 30); // April 30, 2026
  const apr30_2027 = new Date(2027, 3, 30); // April 30, 2027
  const apr30_2028 = new Date(2028, 3, 30); // April 30, 2028
  const may1_2024 = new Date(2024, 4, 1); // May 1, 2024
  const may1_2025 = new Date(2025, 4, 1); // May 1, 2025
  const may1_2026 = new Date(2026, 4, 1); // May 1, 2026
  const may1_2027 = new Date(2027, 4, 1); // May 1, 2027
  const may1_2028 = new Date(2028, 4, 1); // May 1, 2028

  if (launchDate <= apr30_2023) {
    // Special logic: Check if April 30, 2024 is before or equal to (launch date + 1 year)
    const daysUntilApr30_2024 = Math.max(0, (apr30_2024.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    if (apr30_2024 <= anniversaryDate) {
      // If yes, use (days to April 30, 2024 + 1) / 365 × 1%
      return Math.round((daysUntilApr30_2024 / 365) * 1 * 10) / 10 / 100;
    } else {
      // If no, use MIN((days to April 30, 2024 + 1) / 365, 1) × 1%
      return Math.round(Math.min(daysUntilApr30_2024 / 365, 1) * 1 * 10) / 10 / 100;
    }
  } else if (launchDate <= apr30_2024) {
    // 2023_04_30_to_2024_04_30: period1: 0.01, period2: 0.075
    const daysInPeriod1 = Math.max(0, (apr30_2024.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - may1_2024.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const rate = (daysInPeriod1 / 365 * 1) + (daysInPeriod2 / 365 * 7.5);
    return Math.round(rate * 10) / 10 / 100;
  } else if (launchDate <= apr30_2025) {
    // 2024_04_30_to_2025_04_30: period1: 0.075, period2: 0.075
    const daysInPeriod1 = Math.max(0, (apr30_2025.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - may1_2025.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const rate = (daysInPeriod1 / 365 * 7.5) + (daysInPeriod2 / 365 * 7.5);
    return Math.round(rate * 10) / 10 / 100;
  } else if (launchDate <= apr30_2026) {
    // 2025_04_30_to_2026_04_30: period1: 0.075, period2: 0.075
    const daysInPeriod1 = Math.max(0, (apr30_2026.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - may1_2026.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const rate = (daysInPeriod1 / 365 * 7.5) + (daysInPeriod2 / 365 * 7.5);
    return Math.round(rate * 10) / 10 / 100;
  } else if (launchDate <= apr30_2027) {
    // 2026_04_30_to_2027_04_30: period1: 0.075, period2: 0.085
    const daysInPeriod1 = Math.max(0, (apr30_2027.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - may1_2027.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const rate = (daysInPeriod1 / 365 * 7.5) + (daysInPeriod2 / 365 * 8.5);
    return Math.round(rate * 10) / 10 / 100;
  } else if (launchDate <= apr30_2028) {
    // 2027_04_30_to_2028_04_30: period1: 0.085, period2: 0.095
    const daysInPeriod1 = Math.max(0, (apr30_2028.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - may1_2028.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const rate = (daysInPeriod1 / 365 * 8.5) + (daysInPeriod2 / 365 * 9.5);
    return Math.round(rate * 10) / 10 / 100;
  } else {
    // after_2028_04_30: 0.095 (9.5%)
    return 0.095;
  }
};

/**
 * Calculate Group 2 Rate (DOD) for Path 1 (National Contract after August 1, 2024)
 * Uses 3% or 3.25% based on date ranges, with blended rate when 12 months crosses 2026-01-01
 */
const calculateDirectGroup2RatePath1 = (launchDate: Date): number => {
  const dodTransitionDate = new Date(2025, 11, 31); // December 31, 2025
  const dodRateChangeDate = new Date(2026, 0, 1); // January 1, 2026

  // Calculate 12-month period end date: launch date + 12 months - 1 day
  const endOf12Months = new Date(launchDate);
  endOf12Months.setMonth(endOf12Months.getMonth() + 12);
  endOf12Months.setDate(endOf12Months.getDate() - 1);

  if (launchDate <= dodTransitionDate) {
    // before_2025_12_31
    if (endOf12Months <= dodTransitionDate) {
      // if_12months_before_2025_12_31: 0.03 (3%)
      return 0.03;
    } else if (endOf12Months >= dodRateChangeDate) {
      // if_12months_after_2026_01_01: blended_3_to_3.25
      // Calculate percentage of year before 2026-01-01 using ROUNDUP((days before 2026-01-01 / 365) × 100, 0)
      const daysIn2025 = Math.max(0, (dodRateChangeDate.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24));
      const daysIn2026 = Math.max(0, (endOf12Months.getTime() - dodRateChangeDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
      const totalDays = daysIn2025 + daysIn2026;

      if (totalDays === 0) {
        return 0.0325; // 3.25%
      }

      // Calculate percentage before 2026-01-01: ROUNDUP((daysIn2025 / totalDays) × 100, 0)
      const percentBefore2026 = Math.ceil((daysIn2025 / totalDays) * 100);
      const percentAfter2026 = 100 - percentBefore2026;

      // Blend rates: ROUNDUP(((percentage_before × rate_before + percentage_after × rate_after) / 100) / 100, 4 decimals)
      const blendedRate = (percentBefore2026 * 3 + percentAfter2026 * 3.25) / 100;
      const blendedRateDecimal = blendedRate / 100;
      
      // Round up to 4 decimal places
      return Math.ceil(blendedRateDecimal * 10000) / 10000;
    } else {
      // if_12months_after_2025_12_31: 0.0325 (3.25%)
      return 0.0325;
    }
  } else {
    // after_2025_12_31: 0.0325 (3.25%)
    return 0.0325;
  }
};

/**
 * Calculate Group 2 Rate (DOD) for Path 2 (Other cases)
 * Uses 3% or 3.25% based on date ranges, with blended rate when 12 months crosses 2026-01-01
 * Note: Path 2 uses 3.25% for both periods in blended calculation
 */
const calculateDirectGroup2RatePath2 = (launchDate: Date): number => {
  const dodTransitionDate = new Date(2025, 11, 31); // December 31, 2025
  const dodRateChangeDate = new Date(2026, 0, 1); // January 1, 2026

  // Calculate 12-month period end date: launch date + 12 months - 1 day
  const endOf12Months = new Date(launchDate);
  endOf12Months.setMonth(endOf12Months.getMonth() + 12);
  endOf12Months.setDate(endOf12Months.getDate() - 1);

  if (launchDate <= dodTransitionDate) {
    // before_2025_12_31
    if (endOf12Months <= dodTransitionDate) {
      // if_12months_before_2025_12_31: 0.03 (3%)
      return 0.03;
    } else if (endOf12Months >= dodRateChangeDate) {
      // if_12months_after_2026_01_01: blended_3.25_to_3.25
      // Path 2 uses 3.25% for both periods
      // Calculate percentage of year before 2026-01-01 using ROUNDUP((days before 2026-01-01 / 365) × 100, 0)
      const daysIn2025 = Math.max(0, (dodRateChangeDate.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24));
      const daysIn2026 = Math.max(0, (endOf12Months.getTime() - dodRateChangeDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
      const totalDays = daysIn2025 + daysIn2026;

      if (totalDays === 0) {
        return 0.0325; // 3.25%
      }

      // Calculate percentage before 2026-01-01: ROUNDUP((daysIn2025 / totalDays) × 100, 0)
      const percentBefore2026 = Math.ceil((daysIn2025 / totalDays) * 100);
      const percentAfter2026 = 100 - percentBefore2026;

      // Blend rates: ROUNDUP(((percentage_before × 3.25% + percentage_after × 3.25%) / 100) / 100, 4 decimals)
      // Since both rates are 3.25%, the blended rate is always 3.25%
      const blendedRate = (percentBefore2026 * 3.25 + percentAfter2026 * 3.25) / 100;
      const blendedRateDecimal = blendedRate / 100;
      
      // Round up to 4 decimal places
      return Math.ceil(blendedRateDecimal * 10000) / 10000;
    } else {
      // if_12months_after_2025_12_31: 0.0325 (3.25%)
      return 0.0325;
    }
  } else {
    // after_2025_12_31: 0.0325 (3.25%)
    return 0.0325;
  }
};

